import { useBoolean } from '@lib/common';
import { Button, Card, CardActions, CardContent, Grid, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { amber, red } from '@material-ui/core/colors';
import { GenericLocationItem } from '@api/map/types';
import WarningIcon from '@material-ui/icons/Warning';
import { LocationPicker } from './location-picker';
import { useMemo, useCallback } from 'react';
import { ColoredButton } from '@components/colored-elements';
import { rbac } from '@lib/common';

export type LocationCardProps<T extends boolean = boolean> = {
  hideActions?: T;
  value?: Partial<GenericLocationItem>;
  onChange?: T extends true ? never : (value: GenericLocationItem) => any;
  onDelete?: (value: GenericLocationItem) => any;
  title?: React.ReactNode;
};

export function LocationCard<T extends boolean = false>({
  value = {},
  hideActions = false as T,
  onChange = null,
  onDelete = null,
  title = 'Headquarters Location *',
}: LocationCardProps<T>) {
  const location = useMemo(() => value || {}, [value]);
  const { state, city, country, lat, lng } = location;

  const [showLocationPicker, openLocationPicker, closeLocationPicker] = useBoolean(false);

  const handleDelete = useCallback(() => {
    onDelete(location as any);
  }, [onDelete, location]);

  const hasLatLng = lat && lng;

  return (
    <Card>
      <CardContent>
        <Typography style={{ fontSize: 14 }} gutterBottom>
          {title}
        </Typography>
        <Typography color="textSecondary" variant="body2">
          Country
        </Typography>
        <Typography variant="h5" gutterBottom>
          {country || NAText}
        </Typography>
        <Grid container spacing={2}>
          <Grid item>
            <Typography color="textSecondary" variant="body2">
              City
            </Typography>
            <Typography>{city || NAText}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textSecondary" variant="body2">
              State
            </Typography>
            <Typography>{state || NAText}</Typography>
          </Grid>
          <Grid item>
            <Typography color="textSecondary" variant="body2">
              Coordinates
            </Typography>
            {hasLatLng ? (
              <CheckCircleIcon color="primary" />
            ) : (
              <WarningIcon style={{ color: amber[500] }} />
            )}
          </Grid>
        </Grid>
      </CardContent>
      {!hideActions && (
        <>
          <CardActions>
            <Button
              // disabled={rbac('Company_AddContactEdit', '', 'Button')}
              size="small"
              variant="contained"
              color="primary"
              onClick={openLocationPicker}>
              Edit
            </Button>
            {onDelete && (
              <ColoredButton
                onClick={handleDelete}
                size="small"
                variant="contained"
                colored={red[500]}>
                Delete
              </ColoredButton>
            )}
          </CardActions>
          {showLocationPicker && (
            <LocationPicker
              fullWidth
              defaultLocation={location as GenericLocationItem}
              maxWidth="lg"
              open
              onClose={closeLocationPicker}
              onFinish={onChange}
            />
          )}
        </>
      )}
    </Card>
  );
}

const NAText = (
  <em>
    <Typography color="textSecondary" component="span">
      NA
    </Typography>
  </em>
);
